import { useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { useAuth } from '@next-social-platform/chatbot_sso/react';
import { useBot } from '../contexts/BotContext';
import { BotPicker } from './BotPicker';
import { showPlatform, useMe } from '../contexts/MeContext';
import { parseBotPath } from '../lib/botNav';
import { botNavItems } from '../lib/systemScope';

// Per-bot pages live under /bots/:botId/*; system pages are flat. The bot bar is
// the global bot selector — switching navigates the per-bot area to the new bot,
// and landing on a /bots/:botId/* URL syncs the bar back (so deep links work).
const SYSTEM_NAV = [
  { to: '/ai-config', label: 'AI Config' },
  { to: '/llm-pool', label: 'LLM Pool' },
  { to: '/agent', label: 'Agent' },
  { to: '/agent-planner', label: 'Agent Planner' },
  { to: '/agent-workflow', label: 'Workflows' },
  { to: '/llm-providers', label: 'LLM Providers' },
  { to: '/embedding-profiles', label: 'Embedding profiles' },
  { to: '/text-conversion', label: 'Text conversion' },
] as const;

const OPS_NAV = [
  { to: '/session-events', label: 'Session Events' },
  { to: '/hooks', label: 'Hooks' },
  { to: '/eval', label: 'Eval' },
  { to: '/intent-import', label: 'Intent import' },
  { to: '/moderation', label: 'Moderation' },
] as const;

const linkCls = ({ isActive }: { isActive: boolean }) =>
  `rounded px-3 py-2 text-sm font-medium ${
    isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
  }`;

function UserMenu() {
  const { user, logout } = useAuth<{ name?: string; email?: string; username?: string }>();
  const label = user?.name || user?.username || user?.email || 'Signed in';
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="text-gray-600" title={user?.email}>
        {label}
      </span>
      <button
        type="button"
        onClick={() => logout({ mode: 'idp', postLogoutRedirectUri: window.location.origin + '/' })}
        className="rounded border border-gray-300 px-3 py-1 text-gray-700 hover:bg-gray-100"
      >
        Sign out
      </button>
    </div>
  );
}

export function Layout() {
  const { system, botId, setScope } = useBot();
  const location = useLocation();

  // Opening a /bots/:system/:botId/* URL directly moves the scope to that bot.
  const urlBot = parseBotPath(location.pathname);
  useEffect(() => {
    if (urlBot && (urlBot.system !== system || urlBot.botId !== botId)) setScope({ system: urlBot.system, botId: urlBot.botId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlBot?.system, urlBot?.botId]);

  const platform = showPlatform(useMe());
  const botNav = botNavItems({ system, botId });
  const noBot = botNav[0]?.to === null;

  return (
    <div className="flex h-full w-full bg-gray-50 text-gray-900">
      <aside className="flex w-52 flex-col overflow-y-auto border-r border-gray-200 bg-white">
        <div className="px-4 py-4 text-lg font-bold">Integration Hub</div>
        <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
          Bot workspace
        </div>
        <nav className="flex flex-col gap-1 px-2">
          {botNav.map((i) =>
            i.to ? (
              <NavLink key={i.section} to={i.to} className={linkCls} end>
                {i.label}
              </NavLink>
            ) : (
              <span key={i.section} aria-disabled="true" title="Choose a bot first" className="cursor-not-allowed rounded px-3 py-2 text-sm font-medium text-gray-300">
                {i.label}
              </span>
            ),
          )}
        </nav>
        {noBot && <p className="px-4 pt-1 text-xs text-gray-400">Choose a bot in the top bar first.</p>}
        {platform && (
          <>
            <div className="mx-3 mt-6 border-t border-gray-200 px-0 pb-1 pt-3 text-[11px] font-bold uppercase tracking-wide text-gray-500">
              Platform
            </div>
            <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Configuration
            </div>
            <nav className="flex flex-col gap-1 px-2">
              {SYSTEM_NAV.map((i) => (
                <NavLink key={i.to} to={i.to} className={linkCls}>
                  {i.label}
                </NavLink>
              ))}
            </nav>
            <div className="px-3 pb-1 pt-4 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Operations
            </div>
            <nav className="flex flex-col gap-1 px-2 pb-4">
              {OPS_NAV.map((i) => (
                <NavLink key={i.to} to={i.to} className={linkCls}>
                  {i.label}
                </NavLink>
              ))}
            </nav>
          </>
        )}
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-3">
          <BotPicker />
          <UserMenu />
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
